import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import VideoPlayer from "@/components/VideoPlayer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { eventsApi, markupApi, matchApi, ordersApi } from "@/api";
import Preloader from "@/components/Elements/Preloader";
import EventsTable from "@/components/EventLight/EventsTable";
import { createColumnHelper } from "@tanstack/react-table";
import EditableCell from "../Validation/EditableCell";
import {
  convertSecondsToTimestamp,
  getTaskDisplayName,
} from "@/util/functions";
import SubEventsCell from "../Validation/SubEventsCell";
import EventControls from "./EventControls";
import { toast } from "react-toastify";
import ActionCell from "./ActionCell";
import { Button, Spinner, Modal, Form } from "react-bootstrap";
import { LABELER } from "@/constants/routs.constants";
import YouTube from "react-youtube";
import MatchStats from "@/components/EventLight/MatchStats";
import { TEAM_COLORS } from "@/constants/teams";

const columnHelper = createColumnHelper();

const EventLight = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef();
  const { videoId } = useParams();
  const [videoUrl, setVideoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fps, setFps] = useState(1);
  const [events, setEvents] = useState([]);
  const [subEvents, setSubEvents] = useState({});
  const [superTrackUid, setSuperTrackUid] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [player, setPlayer] = useState(null);
  const [activityDate, setActivityDate] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);
  const [actualStartTime, setActualStartTime] = useState(null);
  const [task, setTask] = useState(null);
  const [periodState, setPeriodState] = useState("none");
  const [capturedEventTime, setCapturedEventTime] = useState(null);
  const [stats, setStats] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [timestampOffset, setTimestampOffset] = useState(0);
  const [order, setOrder] = useState(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const [editTeams, setEditTeams] = useState({
    homeTeam: { color: "" },
    guestTeam: { color: "" },
  });
  const [match, setMatch] = useState(null);

  const getCurrentTime = () => {
    return player ? player.getCurrentTime() : 0;
  };

  const opts = {
    width: "100%",
    height: "600",
    playerVars: {
      autoplay: 0,
      controls: 1,
      modestbranding: 1,
      rel: 0,
    },
  };

  const onReady = (event) => {
    setPlayer(event.target);
  };

  // Add keyboard event handler for YouTube player
  useEffect(() => {
    if (!player || !isPro) return;

    const handleKeyDown = (e) => {
      // Only process keyboard shortcuts if no input elements are focused
      if (
        !["INPUT", "TEXTAREA", "SELECT"].includes(
          document.activeElement.tagName
        )
      ) {
        // Space key for play/pause
        if (e.code === "Space") {
          e.preventDefault(); // Prevent page scrolling

          // Toggle play/pause
          const playerState = player.getPlayerState();
          if (playerState === 1) {
            // 1 = playing
            player.pauseVideo();
          } else {
            player.playVideo();
          }
        }

        // 'v' key for 5 seconds forward
        else if (e.code === "KeyV") {
          e.preventDefault();
          const currentTime = player.getCurrentTime();
          player.seekTo(currentTime + 3, true);
        }

        // 'c' key for 5 seconds backward
        else if (e.code === "KeyC") {
          e.preventDefault();
          const currentTime = player.getCurrentTime();
          player.seekTo(Math.max(0, currentTime - 3), true);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [player, isPro]);

  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      const order = await ordersApi.getOrderById(orderId);
      if (!order) return;

      setOrder(order);
      console.log({ order });
    };
    fetchOrder();
  }, [orderId]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        filterFn: "idFilterFn",
      },
      {
        accessorKey: "timestamp",
        id: "Start Time",
        header: "Start Time",
        meta: {
          type: "text",
        },
        filterFn: "startTimeFilterFn",
      },
      {
        accessorKey: "event_type",
        header: "Type",
        cell: EditableCell,
        meta: {
          filterVariant: "select",
          type: "select",
          options: [
            {
              label: "_Pass",
              value: "pass",
            },
            {
              label: "_Save",
              value: "save",
            },
            {
              label: "_Shot",
              value: "shot",
            },
            {
              label: "_Duel",
              value: "duel",
            },
            {
              label: "_Foul",
              value: "foul",
            },
            {
              label: "_Dribble",
              value: "dribble",
            },
            {
              label: "_Inactive ball",
              value: "inactive_ball",
            },
            {
              label: "_Tackle",
              value: "tackle",
            },
          ],
        },
      },
      {
        accessorKey: "success",
        header: "Result",
        cell: EditableCell,
        meta: {
          type: "select",
          options: [
            { label: "_Success", value: true },
            { label: "_Fail", value: false },
          ],
          filterVariant: "select",
        },
        filterFn: "resultFilterFn",
      },
      columnHelper.accessor((row) => row.extra.additional_events, {
        id: "additional_events",
        cell: (props) => (
          <SubEventsCell {...props} fieldName="additional_events" />
        ),
        header: "Additional Events",
        meta: {
          subEvents: subEvents,
        },
        filterFn: "subEventsFilterFn",
      }),
      {
        id: "Actions",
        header: "Actions",
        cell: ActionCell,
      },
    ],
    [subEvents, fps]
  );

  // Add this function to convert timestamp to seconds
  const convertTimestampToSeconds = (timestamp) => {
    if (!timestamp) return 0;
    const timeComponents = timestamp.split(/[:.]/);
    let totalSeconds =
      +timeComponents[0] * 3600 + +timeComponents[1] * 60 + +timeComponents[2];
    if (timeComponents.length === 4) {
      totalSeconds += +timeComponents[3] / 1000000;
    }
    return totalSeconds;
  };

  // Add this function to adjust events with timestamp offset
  const adjustEventTimestamps = (events, offsetInSeconds) => {
    return events.map((event) => ({
      ...event,
      timestamp: convertSecondsToTimestamp(
        convertTimestampToSeconds(event.timestamp) - offsetInSeconds
      ),
    }));
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        let data = await matchApi.getMatch(videoId);
        setMatch(data);
        setOrderId(data.order_id);
        setVideoUrl(data.video?.origin);
        if (data.video?.fps) setFps(data.video?.fps);

        const query = new URLSearchParams(location.search);
        const targetTaskId = query.get("taskId");
        let response = null;
        if (targetTaskId) {
          response = await matchApi.getMatchTask(videoId, targetTaskId);
        } else {
          response = await matchApi.postMatchTask(videoId, "match_event");
        }
        setTask(response);
        const uid = response.task?.super_track_uid;
        setSuperTrackUid(uid);

        // Extract and set timestamp offset
        const timestampOffsetInSeconds = response.task?.timestamp_offset || 0;
        setTimestampOffset(timestampOffsetInSeconds);

        // Set a date 10 minutes in the future
        // const testDate = new Date(Date.now() + 10 * 60 * 1000).toISOString();
        setActivityDate(response.task?.activity_date);

        // Comment out the original code temporarily
        // setIsPro(response.task?.pipeline_passing === "match_analytics_pro");
        // if (response.task?.pipeline_passing === "match_analytics_pro") {
        //   setActivityDate(response.task.activity?.activity_date);
        // }
        const isPro = response.task?.pipeline_passing === "match_analytics_pro";
        setIsPro(isPro);

        if (isPro) {
          const match = response.task?.client_video_source?.match(
            /(?:(?:\?v=|\/embed\/|\.be\/)|\/live\/)([a-zA-Z0-9_-]{11})/
          );
          const videoId = match ? match[1] : null;
          setYoutubeVideoId(videoId);

          if (videoId) {
            try {
              const response = await fetch(
                `https://www.googleapis.com/youtube/v3/videos?part=liveStreamingDetails&id=${videoId}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
              );
              const data = await response.json();
              const actualStartTime =
                data.items?.[0]?.liveStreamingDetails?.actualStartTime;
              if (actualStartTime) {
                setActualStartTime(actualStartTime);
              }
            } catch (error) {
              console.error("Error fetching YouTube stream details:", error);
            }
          }
        }

        let events = await matchApi.getEventsByUid({
          matchId: videoId,
          uid,
        });

        // Adjust event timestamps by subtracting the offset
        const adjustedEvents = adjustEventTimestamps(
          events,
          timestampOffsetInSeconds
        );
        setEvents(adjustedEvents);

        navigate(`/panel/event-light/${videoId}?taskId=${response.id}`);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }

    async function fetchSubEvents() {
      const subEvents = await eventsApi.getSubEvents();
      setSubEvents(subEvents);
    }
    fetchSubEvents();
    fetchData();
  }, [videoId]);

  useEffect(() => {
    if (!activityDate || !isPro) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const startTime = new Date(activityDate).getTime();
      const timeLeft = startTime - now;

      if (timeLeft <= 0) {
        setCountdown("Stream started");
        clearInterval(timer);
      } else {
        const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
        setCountdown(
          `${days}d ${hours}h ${minutes}m ${seconds}s until stream starts`
        );
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [activityDate, isPro]);

  const calculateCurrentTimeAndTimestamp = (useCapturedTime = false) => {
    let currentTime;
    if (!isPro) {
      console.log("noisPro", useCapturedTime, capturedEventTime);
      currentTime =
        useCapturedTime && capturedEventTime !== null
          ? capturedEventTime
          : videoRef.current.currentTime;
    } else {
      console.log("isPro", useCapturedTime, capturedEventTime);
      if (useCapturedTime && capturedEventTime !== null) {
        currentTime = capturedEventTime;
      } else {
        // Calculate the current time using UTC to avoid timezone issues
        const actualStartDate = new Date(actualStartTime);

        // Create UTC start of day
        const startOfDayUTC = new Date(
          Date.UTC(
            actualStartDate.getUTCFullYear(),
            actualStartDate.getUTCMonth(),
            actualStartDate.getUTCDate(),
            0,
            0,
            0,
            0
          )
        );

        // Calculate seconds since start of day in UTC
        const secondsSinceStartOfDay =
          (actualStartDate.getTime() - startOfDayUTC.getTime()) / 1000;

        currentTime = secondsSinceStartOfDay + getCurrentTime();
      }
    }
    return { currentTime };
  };

  const getOrderStats = async (orderId) => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const result = await ordersApi.updateOrderStats(orderId, null);
      setStats(result?.stats || null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong while getting order stats.";
      setStatsError(errorMessage);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    console.log("orderId", orderId, isPro);
    if (!orderId || !isPro) return;

    getOrderStats(orderId);

    // Only set up interval if task is not resolved
    if (!task.resolved_at) {
      const interval = setInterval(() => {
        getOrderStats(orderId);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [orderId, isPro, task]);

  const addStartEndPeriod = async (eventType) => {
    try {
      const payload = {
        event_type: "inactive_ball",
        extra: {
          additional_events: {
            [eventType]: true,
          },
        },
        success: false,
        timestamp: "00:00:00",
      };

      // stUid is always 1 for these events
      const stUid = 1;

      await markupApi.addEvent(videoId, stUid, payload);
      toast.success(`Successfully added ${eventType} event`);

      // Update period state based on which button was pressed
      if (eventType === "start_period") {
        setPeriodState("started");
      } else if (eventType === "end_period") {
        setPeriodState("ended");
      }
    } catch (err) {
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
    }
  };

  const addNewEvent = async (
    selectedEvent,
    selectedOptions,
    multipleSelections,
    callback
  ) => {
    try {
      // Use the captured time instead of current time
      const { currentTime } = calculateCurrentTimeAndTimestamp(true);

      const timestamp = convertSecondsToTimestamp(currentTime);

      const transformedOptions = {
        ...Object.entries(selectedOptions)
          .filter(([key]) => {
            if (selectedEvent === "duel") {
              return key === "initial" ? false : true;
            }
            return !["initial", "success", "fail"].includes(key);
          })
          .reduce((acc, [key, value]) => {
            acc[value] = true;
            return acc;
          }, {}),
        ...Array.from(multipleSelections).reduce((acc, value) => {
          acc[value] = true;
          return acc;
        }, {}),
      };

      const success = selectedOptions.initial === "success";

      let payload = {
        event_type: selectedEvent,
        extra: {
          additional_events: transformedOptions,
          player: null,
        },
        success,
        timestamp,
      };

      if (selectedEvent === "pass") {
        payload = {
          event_type: selectedEvent,
          extra: {
            additional_events: {
              ...transformedOptions,
            },
          },
          success,
          timestamp,
        };
      } else if (selectedEvent === "block_shot") {
        payload = {
          event_type: "shot",
          extra: {
            additional_events: {
              blocked: true,
            },
          },
          success: false,
          timestamp,
        };
      } else if (selectedEvent === "free_kick") {
        payload = {
          event_type: "inactive_ball",
          extra: {
            additional_events: {
              free_kick: true,
            },
          },
          success,
          timestamp,
        };
      } else if (selectedEvent === "penalty") {
        payload = {
          event_type: "inactive_ball",
          extra: {
            additional_events: {
              penalty: true,
            },
          },
          success,
          timestamp,
        };
      } else if (selectedEvent === "azamat") {
        payload = {
          event_type: "inactive_ball",
          extra: {
            additional_events: {
              azamat_throwing: true,
            },
          },
          success,
          timestamp,
        };
      } else if (selectedEvent === "corner") {
        payload = {
          event_type: "inactive_ball",
          extra: {
            additional_events: {
              corner: true,
            },
          },
          success,
          timestamp,
        };
      } else if (selectedEvent === "interception") {
        payload = {
          event_type: "pass",
          extra: {
            additional_events: {
              interception: true,
            },
            player: superTrackUid,
          },
          success: false,
          timestamp,
        };
      } else if (selectedEvent === "recovery") {
        payload = {
          event_type: "pass",
          extra: {
            additional_events: {
              recovery: true,
            },
            player: superTrackUid,
          },
          success: false,
          timestamp,
        };
      } else if (selectedEvent === "save") {
        payload = {
          event_type: "shot",
          extra: {
            additional_events: {
              save: true,
            },
            player: superTrackUid,
          },
          success: true,
          timestamp,
        };
      } else if (selectedEvent === "entries_in_penalty_area") {
        payload = {
          event_type: "entries_in_penalty_area",
          extra: {
            additional_events: {
              with_ball: true,
            },
            player: null,
          },
          success: false,
          timestamp,
        };
      } else if (selectedEvent === "ball_control") {
        payload = {
          event_type: "ball_control",
          extra: {
            additional_events: {},
          },
          success: false,
          timestamp,
        };
      } else if (selectedEvent === "duel_success") {
        payload = {
          event_type: "duel",
          extra: {
            additional_events: {},
          },
          success: true,
          timestamp,
        };
      } else if (selectedEvent === "success_duel") {
        payload = {
          event_type: "duel",
          extra: {
            additional_events: {},
          },
          success: true,
          timestamp,
        };
      }

      let stUid = superTrackUid;
      if (["interception", "recovery", "save"].includes(selectedEvent)) {
        stUid = stUid === 1 ? 2 : 1;
      }

      const response = await markupApi.addEvent(videoId, stUid, payload);
      toast.success(`Successfully added the event ${response.id}`);

      setEvents([response, ...events]);

      // Clear the captured time after successfully adding the event
      setCapturedEventTime(null);

      callback();
    } catch (err) {
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
    }
  };

  // Function to capture the current time when an event is selected
  const captureEventTime = () => {
    if (!isPro) {
      setCapturedEventTime(videoRef.current.currentTime);
    } else {
      // Calculate the current time using UTC to avoid timezone issues
      const actualStartDate = new Date(actualStartTime);
      console.log("actualStartDate", actualStartDate);

      // Create UTC start of day
      const startOfDayUTC = new Date(
        Date.UTC(
          actualStartDate.getUTCFullYear(),
          actualStartDate.getUTCMonth(),
          actualStartDate.getUTCDate(),
          0,
          0,
          0,
          0
        )
      );
      console.log("startOfDayUTC", startOfDayUTC);

      // Calculate seconds since start of day in UTC
      const secondsSinceStartOfDay =
        (actualStartDate.getTime() - startOfDayUTC.getTime()) / 1000;

      console.log("secondsSinceStartOfDay", secondsSinceStartOfDay);
      setCapturedEventTime(secondsSinceStartOfDay + getCurrentTime());
    }
  };

  // Function to clear the captured time
  const clearCapturedEventTime = () => {
    setCapturedEventTime(null);
  };

  const deleteEvent = async (eventId) => {
    try {
      const { id, event_type } = events.find(({ id }) => id === eventId);
      const result = window.confirm(
        `Are you sure you want delete ${event_type} event?`
      );

      if (result) {
        await markupApi.deleteEvent(id);
        toast.success(`Successfully deleted the event ${id}`);
        setEvents((prevEvents) => [
          ...prevEvents.filter(({ id: eventId }) => eventId !== id),
        ]);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        "Something went wrong.";
      toast.error(errorMessage);
    }
  };

  const onVideoLoadHandler = () => {
    const frameTime =
      videoRef.current.webkitDecodedFrameCount / videoRef.current.duration;
    if (fps === 1 || !fps) setFps(Math.round(1 / frameTime));
  };

  const saveAnswer = async () => {
    const result = window.confirm(
      `Are you sure you want to complete this task?`
    );

    if (result) {
      try {
        setIsSaving(true);
        const query = new URLSearchParams(location.search);
        const taskId = query.get("taskId");
        if (!taskId) {
          toast.warning("taskId is not specified.");
          return;
        }

        await matchApi.postMatchTaskResult({
          matchId: videoId,
          taskId: taskId,
          taskResult: {
            status: true,
          },
          taskType: "match_event",
        });
        toast.success("Saved the answer successfully.");
        navigate(LABELER);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          "Something went wrong while saving the answer.";
        toast.error(errorMessage);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Add this function to handle color updates
  const handleColorEdit = async () => {
    try {
      await matchApi.updateTeamColors(videoId, {
        leftTeamColor: editTeams.homeTeam.color,
        rightTeamColor: editTeams.guestTeam.color,
      });

      // Fetch updated match data
      const updatedMatch = await matchApi.getMatch(videoId);
      if (!updatedMatch) return;

      setShowColorModal(false);
      toast.success("Colors updated successfully");
    } catch (err) {
      console.log(err);
      toast.error("Failed to update colors");
    }
  };

  // Add this function to open the color modal
  const openColorModal = () => {
    const leftColor = match?.left_team_jersey_color || match?.match_colors?.[0];
    const rightColor =
      match?.right_team_jersey_color || match?.match_colors?.[1];

    setEditTeams({
      homeTeam: { color: leftColor || "" },
      guestTeam: { color: rightColor || "" },
    });
    setShowColorModal(true);
  };

  // Add this function to render color options
  const renderColorOptions = useCallback(() => {
    if (!match?.match_colors?.length) {
      return <option disabled>No colors available</option>;
    }

    return TEAM_COLORS.filter((color) =>
      match.match_colors.includes(color.value)
    ).map((color) => (
      <option key={color.value} value={color.value}>
        {color.label}
      </option>
    ));
  }, [match?.match_colors]);

  if (isLoading) {
    return <Preloader className="tw-m-auto" />;
  }
  //https://www.youtube.com/watch?v=xf3gAFclwqo
  return (
    <div className="tw-p-10 tw-pt-4">
      <div className="tw-flex tw-gap-2 tw-items-center tw-justify-between tw-mb-4">
        <div className="tw-text-center tw-text-3xl tw-font-bold">
          {task && getTaskDisplayName(task)}
        </div>
        {isPro && (
          <MatchStats
            order={order}
            stats={stats}
            isLoading={statsLoading}
            error={statsError}
          />
        )}
        <div className="tw-flex tw-gap-4 tw-items-center tw-justify-center">
          {isPro && countdown && (
            <div className="tw-text-lg tw-font-semibold">{countdown}</div>
          )}
          <Button
            variant="outline-success"
            size="sm"
            className="mx-1 my-1"
            onClick={openColorModal}
          >
            Edit Color
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={isSaving}
            onClick={saveAnswer}
          >
            {isSaving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Saving
              </>
            ) : (
              "Complete Task"
            )}
          </Button>
        </div>
      </div>
      <div className="tw-relative tw-w-full">
        {isPro ? (
          <>
            {youtubeVideoId && (
              <YouTube videoId={youtubeVideoId} opts={opts} onReady={onReady} />
            )}
          </>
        ) : (
          <VideoPlayer
            src={videoUrl}
            ref={videoRef}
            fps={fps}
            step={fps}
            videoId={videoId}
            videoProps={{
              autoPlay: false,
              loop: false,
            }}
            onVideoLoadHandler={onVideoLoadHandler}
          />
        )}
        <div
          className="tw-absolute tw-left-4 tw-bottom-12 tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-2"
          style={{ bottom: isPro ? 60 : 48 }}
        >
          <div className="">
            <EventControls
              isPro={isPro}
              addNewEvent={addNewEvent}
              onEventTimeCapture={captureEventTime}
              onEventTimeCaptureClear={clearCapturedEventTime}
            />
          </div>
        </div>
      </div>
      <div className="tw-max-h-[400px] tw-overflow-y-auto">
        {isPro && (
          <div className="tw-flex tw-gap-4 tw-mb-2">
            <Button
              variant="success"
              size="sm"
              onClick={() => addStartEndPeriod("start_period")}
              // disabled={periodState === "started"}
              // style={{ opacity: periodState === "started" ? 0.6 : 1 }}
            >
              Start period
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={() => addStartEndPeriod("end_period")}
              // disabled={periodState === "ended" || periodState === "none"}
              // style={{ opacity: periodState === "ended" || periodState === "none" ? 0.6 : 1 }}
            >
              End period
            </Button>
          </div>
        )}
        <EventsTable
          columns={columns}
          events={events}
          deleteEvent={deleteEvent}
        />
      </div>

      <Modal show={showColorModal} onHide={() => setShowColorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Team Colors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-gap-4">
            <div className="mb-3">
              <div className="tw-w-32 mb-2">Left team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.homeTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    homeTeam: { ...editTeams.homeTeam, color: e.target.value },
                  })
                }
              >
                <option value="">Select color</option>
                {renderColorOptions()}
              </Form.Select>
            </div>
            <div>
              <div className="tw-w-32 mb-2">Right team color:</div>
              <Form.Select
                className="tw-w-36"
                value={editTeams.guestTeam.color}
                onChange={(e) =>
                  setEditTeams({
                    ...editTeams,
                    guestTeam: {
                      ...editTeams.guestTeam,
                      color: e.target.value,
                    },
                  })
                }
              >
                <option value="">Select color</option>
                {renderColorOptions()}
              </Form.Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColorModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleColorEdit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventLight;
